import PropTypes from 'prop-types';
import React, { useState, useContext, useMemo, useCallback } from 'react';
import { Input } from '@eika/input';
import DropDown from '../../../drop-down';
import CountryContext from '../../../country-context';
import getLabel, { getHelpBody } from '../labels';
import { getCitizenshipList } from '../../helper/component-helper';
import { CITIZENSHIP, SECOND_CITIZENSHIP, RESIDENCE_COUNTRY, NORWAY, POSTAL_CODE } from '../keys-constants';
import ahvContext from '../../../ahv-context';
import ErrorMessage from '../../errormessage';

export const getAktorDetailsFields = () => ({
    navn: null,
    fnr: null,
    fodeland: null,
    statsborgerskap: null,
    sekundartStatsborgerskap: null,
    adresse: null,
    postnummer: null,
    poststed: null,
    bostedsland: null,
});

const getIsPreFilled = (preFilledAktor, path, pattern = /^.*$/) =>
    !!preFilledAktor[path] && pattern.test(preFilledAktor[path]);

function AktorDetaljer({ config = {}, errors, question, onChange, tekster = {}, index, prefilledAktor }) {
    const countryContext = useContext(CountryContext);
    const context = useContext(ahvContext);
    const { getHasSubmitted } = context;
    const bostedslandOptions = useMemo(() => countryContext.getBostedslandOptions(), [countryContext]);
    const fodelandOptions = useMemo(() => countryContext.getFodelandOptions(), [countryContext]);
    const norwayPostalCodesList = useMemo(() => countryContext.getNorwegianPostalCodesOptions, [countryContext]);

    const [citizenship, setCitizenship] = useState(question.statsborgerskap);
    const [secondCitizenship, setSecondCitizenship] = useState(question.sekundartStatsborgerskap);
    const [residenceCountry, setResidenceCountry] = useState(question.bostedsland);
    const [postalCode, setPostalCode] = useState(question.postnummer);
    const listOfCountries = countryContext.getStatsborgerskapOptions();

    const getHandleChange = useCallback(
        (key) => (event) => {
            const value = event?.target?.value ?? event;
            if (key === CITIZENSHIP) {
                setCitizenship(value);
            }
            if (key === SECOND_CITIZENSHIP) {
                setSecondCitizenship(value);
            }
            if (key === RESIDENCE_COUNTRY && value !== '') {
                setResidenceCountry(value);
            }
            if (key === POSTAL_CODE && value !== '') {
                setPostalCode(value);
            }
            if (event?.target?.name?.includes('fnr')) {
                onChange({ [key]: `${value}`.replace(new RegExp(/[^0-9]/, 'g'), '') });
            } else {
                onChange({ [key]: value });
            }
        },
        [onChange]
    );

    const removeValue = useCallback(
        (key) => {
            if (key === CITIZENSHIP) {
                setCitizenship(undefined);
            }
            if (key === SECOND_CITIZENSHIP) {
                setSecondCitizenship(undefined);
            }
            if (key === RESIDENCE_COUNTRY) {
                setResidenceCountry(undefined);
            }
            onChange({ [key]: undefined });
        },
        [onChange]
    );

    const isCountryNorway = (val) => val === NORWAY;

    const errorTextPostalCode = () => {
        if (errors.includes('postnummer')) {
            return getLabel(tekster, 'aktor_postnummer_instruks');
        }
        if (
            getHasSubmitted &&
            isCountryNorway(residenceCountry) &&
            norwayPostalCodesList &&
            !norwayPostalCodesList.includes(postalCode)
        ) {
            return getLabel(tekster, 'aktor_norwegian_postal_code_instruks');
        }
        return null;
    };

    const entryKey = index != null ? `${index}-${question.aktorKey}` : question.aktorKey;

    return (
        <>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <Input
                    className="aktor-field-navn"
                    name={`navn-aktor-${entryKey}`}
                    onChange={getHandleChange('navn')}
                    value={question.navn || ''}
                    modalHelpHeader={tekster.aktor_navn_infotittel}
                    modalHelpBody={getHelpBody(tekster, 'aktor_navn_info')}
                    disabled={getIsPreFilled(prefilledAktor, 'navn')}
                    type="text"
                    label={getLabel(tekster, 'aktor_navn')}
                />
                {errors.includes('navn') && <ErrorMessage text={getLabel(tekster, 'aktor_navn_instruks')} />}
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <Input
                    className="aktor-field-fnr"
                    name={`fnr-aktor-${entryKey}`}
                    onChange={getHandleChange('fnr')}
                    value={question.fnr || ''}
                    modalHelpHeader={tekster.aktor_fnr_infotittel}
                    modalHelpBody={getHelpBody(tekster, 'aktor_fnr_info')}
                    disabled={getIsPreFilled(prefilledAktor, 'fnr', /\d{11}/)}
                    type="text"
                    label={getLabel(tekster, 'aktor_fnr')}
                />
                {errors.includes('fnr') && <ErrorMessage text={getLabel(tekster, 'aktor_fnr_instruks')} />}
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <label>{getLabel(tekster, 'aktor_fodeland')}</label>
                <DropDown
                    name={`birth-country-aktor-${entryKey}`}
                    values={fodelandOptions}
                    selectValue={getHandleChange('fodeland')}
                    selected={question.fodeland || ''}
                    modalHelpHeader={tekster.aktor_fodeland_infotittel}
                    modalHelpBody={getHelpBody(tekster, 'aktor_fodeland_info')}
                    disabled={getIsPreFilled(prefilledAktor, 'fodeland')}
                    removeValue={() => removeValue('fodeland')}
                />
                {errors.includes('fodeland') && <ErrorMessage text={getLabel(tekster, 'aktor_fodeland_instruks')} />}
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <label>{getLabel(tekster, 'aktor_statsborgerskap')}</label>
                <DropDown
                    name={`citizenship-aktor-${entryKey}`}
                    values={getCitizenshipList(listOfCountries, secondCitizenship)}
                    selectValue={getHandleChange('statsborgerskap')}
                    selected={question.statsborgerskap || ''}
                    modalHelpHeader={tekster.aktor_statsborgerskap_infotittel}
                    modalHelpBody={getHelpBody(tekster, 'aktor_statsborgerskap_info')}
                    disabled={getIsPreFilled(prefilledAktor, 'statsborgerskap')}
                    removeValue={() => removeValue('statsborgerskap')}
                />
                {errors.includes('statsborgerskap') && (
                    <ErrorMessage text={getLabel(tekster, 'aktor_statsborgerskap_instruks')} />
                )}
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <label>{getLabel(tekster, 'aktor_statsborgerskap2')}</label>
                <DropDown
                    name={`second-citizenship-aktor-${entryKey}`}
                    values={getCitizenshipList(listOfCountries, citizenship)}
                    selectValue={getHandleChange('sekundartStatsborgerskap')}
                    selected={question.sekundartStatsborgerskap || ''}
                    modalHelpHeader={tekster.aktor_statsborgerskap2_infotittel}
                    modalHelpBody={getHelpBody(tekster, 'aktor_statsborgerskap2_info')}
                    disabled={getIsPreFilled(prefilledAktor, 'sekundartStatsborgerskap')}
                    removeValue={() => removeValue('sekundartStatsborgerskap')}
                />
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <Input
                    className="aktor-field-adresse"
                    name={`adresse-aktor-${entryKey}`}
                    onChange={getHandleChange('adresse')}
                    value={question.adresse || ''}
                    modalHelpHeader={tekster.aktor_adresse_infotittel}
                    modalHelpBody={getHelpBody(tekster, 'aktor_adresse_info')}
                    disabled={getIsPreFilled(prefilledAktor, 'adresse')}
                    type="text"
                    label={getLabel(tekster, 'aktor_adresse', config.addressInformationOptional)}
                />
                {errors.includes('adresse') && <ErrorMessage text={getLabel(tekster, 'aktor_adresse_instruks')} />}
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <div className="row row-no-gutters">
                    <div className="col-xs-4">
                        <Input
                            className="aktor-field-postnummer"
                            name={`postnummer-aktor-${entryKey}`}
                            onChange={getHandleChange('postnummer')}
                            value={question.postnummer || ''}
                            modalHelpHeader={tekster.aktor_postnummer_infotittel}
                            modalHelpBody={getHelpBody(tekster, 'aktor_postnummer_info')}
                            disabled={getIsPreFilled(prefilledAktor, 'postnummer')}
                            type="text"
                            label={getLabel(tekster, 'aktor_postnummer', config.addressInformationOptional)}
                        />
                        {errorTextPostalCode() && <ErrorMessage text={errorTextPostalCode()} />}
                    </div>
                    <div className="col-xs-7 col-xs-offset-1">
                        <Input
                            className="aktor-field-poststed"
                            name={`poststed-aktor-${entryKey}`}
                            onChange={getHandleChange('poststed')}
                            value={question.poststed || ''}
                            modalHelpHeader={tekster.aktor_poststed_infotittel}
                            modalHelpBody={getHelpBody(tekster, 'aktor_poststed_info')}
                            disabled={getIsPreFilled(prefilledAktor, 'poststed')}
                            type="text"
                            label={getLabel(tekster, 'aktor_poststed', config.addressInformationOptional)}
                        />
                        {errors.includes('poststed') && (
                            <ErrorMessage text={getLabel(tekster, 'aktor_poststed_instruks')} />
                        )}
                    </div>
                </div>
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <label>{getLabel(tekster, 'aktor_bostedsland', config.addressInformationOptional)}</label>
                <DropDown
                    name={`residence-aktor-${entryKey}`}
                    values={bostedslandOptions}
                    selectValue={getHandleChange('bostedsland')}
                    selected={question.bostedsland || ''}
                    modalHelpHeader={tekster.aktor_bostedsland_infotittel}
                    modalHelpBody={getHelpBody(tekster, 'aktor_bostedsland_info')}
                    disabled={getIsPreFilled(prefilledAktor, 'bostedsland')}
                    removeValue={() => removeValue('bostedsland')}
                />
                {errors.includes('bostedsland') && (
                    <ErrorMessage text={getLabel(tekster, 'aktor_bostedsland_instruks')} />
                )}
            </div>
        </>
    );
}

AktorDetaljer.propTypes = {
    config: PropTypes.shape({
        addressInformationOptional: PropTypes.bool,
    }),
    errors: PropTypes.arrayOf(PropTypes.string),
    question: PropTypes.shape({
        navn: PropTypes.string,
        fnr: PropTypes.string,
        fodeland: PropTypes.string,
        statsborgerskap: PropTypes.string,
        sekundartStatsborgerskap: PropTypes.string,
        adresse: PropTypes.string,
        postnummer: PropTypes.string,
        poststed: PropTypes.string,
        bostedsland: PropTypes.string,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    tekster: PropTypes.shape({
        aktor_add: PropTypes.string,
        aktor_adresse_instruks: PropTypes.string,
        aktor_adresse: PropTypes.string,
        aktor_aktor_add: PropTypes.string,
        aktor_bostedsland_instruks: PropTypes.string,
        aktor_bostedsland: PropTypes.string,
        aktor_card_title: PropTypes.string,
        aktor_fnr_instruks: PropTypes.string,
        aktor_fnr: PropTypes.string,
        aktor_fodeland_instruks: PropTypes.string,
        aktor_fodeland: PropTypes.string,
        aktor_navn_instruks: PropTypes.string,
        aktor_navn: PropTypes.string,
        aktor_postnummer_instruks: PropTypes.string,
        aktor_postnummer: PropTypes.string,
        aktor_poststed_instruks: PropTypes.string,
        aktor_poststed: PropTypes.string,
        aktor_statsborgerskap_instruks: PropTypes.string,
        aktor_statsborgerskap: PropTypes.string,
        aktor_statsborgerskap2: PropTypes.string,
    }).isRequired,
    prefilledAktor: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]).isRequired,
    index: PropTypes.number,
};

export default AktorDetaljer;
